import React from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import Footer from "common/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  useMaterialUIController,
  setTransparentSidenav,
  setWhiteSidenav,
  setFixedNavbar,
  setSidenavColor,
  setDarkMode,
} from "context";
import { useLanguage } from "context/LanguageContext";

const sidenavColors = ["primary", "dark", "info", "success", "warning", "error"];

function SettingsPage() {
  const navigate = useNavigate();
  const { t, currentLanguage, changeLanguage } = useLanguage();
  const [controller, dispatch] = useMaterialUIController();
  const { fixedNavbar, sidenavColor, transparentSidenav, whiteSidenav, darkMode } = controller;

  const currentSidenavType = whiteSidenav ? "white" : transparentSidenav ? "transparent" : "dark";

  const handleSidenavTypeChange = (type) => {
    if (type === "transparent") {
      setTransparentSidenav(dispatch, true);
      setWhiteSidenav(dispatch, false);
      return;
    }

    if (type === "white") {
      setWhiteSidenav(dispatch, true);
      setTransparentSidenav(dispatch, false);
      return;
    }

    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
  };

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                px={3}
                py={2.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    {t("settings")}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    {t("customizeApplicationPreferences")}
                  </MDTypography>
                </MDBox>
                <Tooltip title={t("backToDocuments") || "Back to Documents"}>
                  <IconButton color="info" onClick={() => navigate(-1)}>
                    <Icon>arrow_back</Icon>
                  </IconButton>
                </Tooltip>
              </MDBox>
              <Divider />
              <MDBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={7}>
                    <Card
                      sx={{
                        height: "100%",
                        boxShadow: "none",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <MDBox p={3}>
                        <MDTypography variant="h6" mb={2}>
                          {t("appearance") || "Appearance"}
                        </MDTypography>

                        <MDBox mb={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>{t("language")}</InputLabel>
                            <Select
                              value={currentLanguage}
                              label={t("language")}
                              onChange={(event) => changeLanguage(event.target.value)}
                            >
                              <MenuItem value="en">{t("english")}</MenuItem>
                              <MenuItem value="lt">{t("lithuanian")}</MenuItem>
                            </Select>
                          </FormControl>
                        </MDBox>

                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          py={1}
                        >
                          <MDBox>
                            <MDTypography variant="button" fontWeight="medium">
                              {t("lightDark")}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              {t("themeModeDescription") ||
                                "Switch between light and dark interface."}
                            </MDTypography>
                          </MDBox>
                          <Switch
                            checked={darkMode}
                            onChange={() => setDarkMode(dispatch, !darkMode)}
                          />
                        </MDBox>

                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          py={1}
                        >
                          <MDBox>
                            <MDTypography variant="button" fontWeight="medium">
                              {t("navbarFixed")}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              {t("navbarFixedDescription") ||
                                "Keep the top navigation visible while scrolling."}
                            </MDTypography>
                          </MDBox>
                          <Switch
                            checked={fixedNavbar}
                            onChange={() => setFixedNavbar(dispatch, !fixedNavbar)}
                          />
                        </MDBox>

                        <MDBox mt={3}>
                          <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
                            {t("sidenavType")}
                          </MDTypography>
                          <MDBox display="flex" gap={1} flexWrap="wrap">
                            {["dark", "transparent", "white"].map((type) => (
                              <MDButton
                                key={type}
                                variant={currentSidenavType === type ? "contained" : "outlined"}
                                color="dark"
                                size="small"
                                onClick={() => handleSidenavTypeChange(type)}
                                sx={{ textTransform: "none" }}
                              >
                                {t(type)}
                              </MDButton>
                            ))}
                          </MDBox>
                        </MDBox>

                        <MDBox mt={3}>
                          <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
                            {t("sidenavColors")}
                          </MDTypography>
                          <MDBox display="flex" gap={1} flexWrap="wrap">
                            {sidenavColors.map((color) => (
                              <IconButton
                                key={color}
                                onClick={() => setSidenavColor(dispatch, color)}
                                sx={(theme) => ({
                                  width: 28,
                                  height: 28,
                                  border: "2px solid",
                                  borderColor:
                                    sidenavColor === color
                                      ? theme.palette.text.primary
                                      : theme.palette.divider,
                                  backgroundImage: theme.functions.linearGradient(
                                    theme.palette.gradients[color].main,
                                    theme.palette.gradients[color].state
                                  ),
                                  borderRadius: "50%",
                                })}
                              />
                            ))}
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12} lg={5}>
                    <Card sx={{ boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
                      <MDBox p={3}>
                        <MDTypography variant="h6" mb={2}>
                          {t("quickLinks") || "Quick Links"}
                        </MDTypography>
                        <MDBox display="flex" flexDirection="column" gap={1.5}>
                          <MDButton
                            variant="outlined"
                            color="info"
                            startIcon={<Icon>password</Icon>}
                            onClick={() => {
                              window.location.href = "/settings/password_change/";
                            }}
                            sx={{ justifyContent: "flex-start" }}
                          >
                            {t("changePassword") || "Change Password"}
                          </MDButton>
                          <MDButton
                            variant="outlined"
                            color="info"
                            startIcon={<Icon>smart_toy</Icon>}
                            onClick={() => navigate("/llm-settings")}
                            sx={{ justifyContent: "flex-start" }}
                          >
                            {t("llmSettings")}
                          </MDButton>
                          <MDButton
                            variant="outlined"
                            color="info"
                            startIcon={<Icon>description</Icon>}
                            onClick={() => navigate("/documents")}
                            sx={{ justifyContent: "flex-start" }}
                          >
                            {t("documents")}
                          </MDButton>
                        </MDBox>

                        <Divider sx={{ my: 3 }} />

                        <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
                          {t("settingsSavedAutomatically") || "Settings are saved automatically"}
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                          {t("settingsSavedAutomaticallyDescription") ||
                            "Your interface preferences are stored locally in this browser and applied automatically next time."}
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SettingsPage;
